import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { supabase } from "../../supaBaseClient";
import { API_BASE } from "../../config";
import Footer from "../homepage/footer";

const QuizAttempts = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();

  const [attempts, setAttempts] = useState([]);
  const [topicName, setTopicName] = useState("");
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAttempts = async () => {
      setLoading(true);
      setInfo("");
      setError("");

      const { data: userData, error: userErr } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (userErr || !userId) {
        setInfo("Please sign in to view your quiz history.");
        setAttempts([]);
        setLoading(false);
        return;
      }

      try {
        // Fetch topic name if topicId exists
        if (topicId) {
          const topicRes = await axios.get(`${API_BASE}/api/topics/${topicId}`);
          setTopicName(topicRes.data.name || "Unknown Topic");
        }

        const res = await axios.get(`${API_BASE}/api/quiz-attempts/user/${userId}`);

        const rows = Array.isArray(res.data) ? res.data : [];
        const filtered = topicId ? rows.filter(r => r.topic_id === Number(topicId)) : rows;

        if (filtered.length === 0) {
          setInfo("You haven't completed any quizzes for this topic yet.");
          setAttempts([]);
        } else {
          // Sort by most recent first
          setAttempts(filtered.sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          ));
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setInfo("You haven't completed any quizzes for this topic yet.");
          setAttempts([]);
        } else {
          console.error("Error fetching quiz attempts:", err);
          setError("We couldn't load your quiz history right now. Please try again later.");
          setAttempts([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAttempts();
  }, [topicId]);

  // Calculate statistics
  const calculateStats = () => {
    if (attempts.length === 0) return { total: 0, passed: 0, passRate: 0, bestScore: 0, avgScore: 0 };

    const passed = attempts.filter(a => a.is_passed).length;
    const passRate = ((passed / attempts.length) * 100).toFixed(1);
    const scores = attempts.map(a => (a.score / a.max_score) * 100);
    const bestScore = Math.max(...scores).toFixed(1);
    const avgScore = (scores.reduce((sum, s) => sum + s, 0) / scores.length).toFixed(1);

    return {
      total: attempts.length,
      passed,
      passRate,
      bestScore,
      avgScore
    };
  };

  const stats = calculateStats();

  const startNewAttempt = () => {
    if (!topicId) return;
    navigate(`/quizzes/${topicId}`);
  };

  const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getBadgeColor = (isPassed) => {
    return isPassed 
      ? 'bg-green-100 text-green-700 border-green-300' 
      : 'bg-red-100 text-red-700 border-red-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600 font-semibold">Loading quiz attempts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>Quiz Attempts | MLMaths</title>
        <link rel="canonical" href={`https://mlmaths.co.uk/quizattempts${topicId ? `/${topicId}` : ''}`} />
        <meta name="description" content="View your quiz attempt history, see your scores, and start new attempts to improve your GCSE Maths skills." />
      </Helmet>

      <main className="container mx-auto py-10 px-4 sm:px-6 lg:px-12 max-w-7xl flex-grow">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {topicName ? `${topicName} - Quiz Attempts` : 'Quiz Attempts'}
          </h1>
          <p className="text-lg text-gray-600">Review your performance and track improvements</p>
        </div>

        {/* Statistics Cards */}
        {attempts.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border-l-4 border-teal-500">
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">Total Attempts</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-800">{stats.total}</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border-l-4 border-green-500">
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">Pass Rate</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-800">{stats.passRate}%</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border-l-4 border-blue-500">
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">Best Score</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-800">{stats.bestScore}%</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border-l-4 border-purple-500">
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">Average</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-800">{stats.avgScore}%</p>
            </div>
          </div>
        )}

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-t-4 border-teal-500">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Attempt History</h2>
                <p className="text-gray-600 mt-1">All your quiz attempts for this topic</p>
              </div>
              {topicId && attempts.length > 0 && (
                <button
                  onClick={startNewAttempt}
                  className="hidden md:flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Start New Attempt
                </button>
              )}
            </div>
          </div>

          {error ? (
            <div className="p-12 text-center">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-600 font-semibold">{error}</p>
            </div>
          ) : attempts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium text-lg mb-2">{info || "No quiz attempts yet"}</p>
              <p className="text-gray-500 text-sm mb-6">Take your first quiz to see your results here</p>
              {topicId && (
                <button
                  onClick={startNewAttempt}
                  className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold"
                >
                  Start First Attempt
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop: Compact Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-teal-500 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">Attempt</th>
                      <th className="px-6 py-4 text-center font-semibold">Score</th>
                      <th className="px-6 py-4 text-center font-semibold">Percentage</th>
                      <th className="px-6 py-4 text-center font-semibold">Result</th>
                      <th className="px-6 py-4 text-center font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {attempts.map((attempt, idx) => {
                      const percentage = ((attempt.score / attempt.max_score) * 100).toFixed(1);
                      return (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="bg-teal-100 w-10 h-10 rounded-full flex items-center justify-center">
                                <span className="text-teal-600 font-bold text-sm">#{attempt.attempt_number}</span>
                              </div>
                              <span className="font-semibold text-gray-800">Attempt {attempt.attempt_number}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`font-bold text-lg ${getScoreColor(attempt.score, attempt.max_score)}`}>
                              {attempt.score}
                            </span>
                            <span className="text-gray-500"> / {attempt.max_score}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-gray-700 font-semibold">{percentage}%</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${getBadgeColor(attempt.is_passed)}`}>
                              {attempt.is_passed ? '✓ Passed' : '✗ Failed'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center text-gray-600">
                            {new Date(attempt.created_at).toLocaleDateString("en-GB", {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile: Compact Cards */}
              <div className="md:hidden p-4 space-y-3">
                {attempts.map((attempt, idx) => {
                  const percentage = ((attempt.score / attempt.max_score) * 100).toFixed(1);
                  return (
                    <div
                      key={idx}
                      className="bg-gray-50 rounded-xl p-4 border-l-4 border-teal-500"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="bg-teal-100 w-8 h-8 rounded-full flex items-center justify-center">
                            <span className="text-teal-600 font-bold text-xs">#{attempt.attempt_number}</span>
                          </div>
                          <span className="font-bold text-gray-800">Attempt {attempt.attempt_number}</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getBadgeColor(attempt.is_passed)}`}>
                          {attempt.is_passed ? '✓ Pass' : '✗ Fail'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Score</p>
                          <p className={`font-bold ${getScoreColor(attempt.score, attempt.max_score)}`}>
                            {attempt.score} / {attempt.max_score}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Percentage</p>
                          <p className="font-bold text-gray-800">{percentage}%</p>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(attempt.created_at).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Mobile: Start New Attempt Button */}
              {topicId && (
                <div className="md:hidden p-4 border-t border-gray-200">
                  <button
                    onClick={startNewAttempt}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-semibold"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Start New Attempt
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Performance Summary Card */}
        {attempts.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white bg-opacity-20 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold">
                  {stats.passRate >= 80 ? "Excellent Work!" :
                   stats.passRate >= 60 ? "Good Progress!" :
                   "Keep Practising!"}
                </h3>
                <p className="text-teal-100">
                  {stats.passRate >= 80 ? "You're mastering this topic" :
                   stats.passRate >= 60 ? "You're improving steadily" :
                   "Every attempt makes you stronger"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-sm text-teal-100 mb-1">Attempts</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-sm text-teal-100 mb-1">Pass Rate</p>
                <p className="text-2xl font-bold">{stats.passRate}%</p>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-sm text-teal-100 mb-1">Best Score</p>
                <p className="text-2xl font-bold">{stats.bestScore}%</p>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default QuizAttempts;